import React, { useEffect, useMemo, useState } from 'react';
import toast from '../../shared/Toast';
import { Icon } from '../BaileyBits';
import { Section } from '../BaileyHome';
import { hapticError, hapticTick } from '../../../_utils/haptics';
import MessageCard from './MessageCard';
import AnswerPanel from './AnswerPanel';
import { ConversationPanel, ConversationRow } from './ConversationPanels';
import { useNeeds, useNeedActions, NeedsCards } from './NeedsYou';
import { groupByDay, matches } from './transcript';

/**
 * Every conversation Bailey has had with clients, and every message it took:
 * All, Calls, Chats and Messages, grouped by day, with search over what's
 * loaded and older pages on request.
 */
const FILTERS = [['all', 'All'], ['call', 'Calls'], ['chat', 'Chats'], ['msg', 'Messages']];

const messageMatches = (m, q) => {
    const s = String(q || '').trim().toLowerCase();
    if (!s) return true;
    return [m.name, m.contact, m.text].join(' ').toLowerCase().includes(s);
};

// A person matches when any of their messages does.
const personMatches = (g, q) => g.messages.some((m) => messageMatches(m, q));

export default function ConversationsPage({ fd, startOn = 'all', history, salonName, look }) {
    const [filter, setFilter] = useState(startOn);
    // On Messages: everyone waiting, or just those waiting for one thing.
    const [kind, setKind] = useState('all');
    const [q, setQ] = useState('');
    const [panel, setPanel] = useState(null);   // { conversation } | { add }
    const people = useNeeds(fd);
    const needs = useNeedActions({ fd, groups: people, history, salonName });
    // Once no one is waiting, a chosen filter goes too, so it can't still be on, out of sight, when the next message comes.
    useEffect(() => { if (!people.length) setKind('all'); }, [people.length]);

    const shown = useMemo(() => fd.conversations.filter((c) => (
        (filter === 'all' || (filter === 'call') === (c.channel === 'phone')) && matches(c, q)
    )), [fd.conversations, filter, q]);
    const groups = useMemo(() => groupByDay(shown), [shown]);
    const doneList = useMemo(() => fd.messages
        .filter((m) => fd.done[m.id])
        .sort((a, b) => fd.done[b.id] - fd.done[a.id]), [fd.messages, fd.done]);

    if (!fd.loaded) {
        return (
            <div className="bly-home fd-page" aria-busy="true" aria-label="Loading conversations">
                <div className="bly-card fd-wait" style={{ height: 40 }} />
                <div className="bly-card fd-wait" style={{ height: 260 }} />
            </div>
        );
    }

    const openPeople = people.filter((g) => personMatches(g, q));
    // The ones already done aren't sorted by what they were waiting for, so a filter leaves them out.
    const doneMsgs = kind === 'all' ? doneList.filter((m) => messageMatches(m, q)) : [];
    const searching = !!q.trim();

    let empty = '';
    if (filter !== 'msg' && !shown.length) {
        if (searching) empty = `Nothing matches “${q.trim()}”${fd.conversationsMore ? ' in what’s loaded. Load older conversations to look further back.' : '.'}`;
        else if (!fd.conversations.length) empty = fd.on ? 'When clients chat or call, you can read every word here.' : 'Nothing yet. Switch on chat or the phone on the front desk, and every conversation shows up here.';
        else empty = filter === 'call' ? 'No calls in what’s loaded.' : 'No chats in what’s loaded.';
    }

    return (
        <div className="bly-home fd-page fd-convs">
            {fd.error && <div className="bly-error" role="alert">{fd.error}</div>}

            <div className="fd-tools">
                <div className="fd-filter" role="group" aria-label="Show">
                    {FILTERS.map(([id, label]) => (
                        <button key={id} type="button" aria-pressed={filter === id} onClick={() => { hapticTick(); setFilter(id); }}>
                            {label}
                            {id === 'msg' && fd.open.length > 0 && <span className="bly-count">{fd.open.length}</span>}
                        </button>
                    ))}
                </div>
                <label className="fd-search" htmlFor="fd-conv-q">
                    <Icon.Search size={16} />
                    <input id="fd-conv-q" type="search" value={q} autoComplete="off" placeholder={filter === 'msg' ? 'Search messages' : 'Search names, numbers and words'}
                        aria-label={filter === 'msg' ? 'Search messages' : 'Search conversations'}
                        onChange={(e) => setQ(e.target.value)} />
                </label>
            </div>

            {filter !== 'msg' && (
                <>
                    {groups.map((g) => (
                        <Section key={g.label} label={g.label}>
                            <div className="bly-card bly-list">
                                {g.items.map((c) => <ConversationRow key={c.id} c={c} timeOnly onOpen={(x) => setPanel({ conversation: x })} />)}
                            </div>
                        </Section>
                    ))}
                    {empty && <p className="fd-empty">{empty}</p>}
                    {fd.conversationsMore && fd.conversations.length > 0 && (
                        <div className="fd-older">
                            <button type="button" className="bly-btn is-secondary" disabled={fd.conversationsBusy} onClick={async () => { hapticTick(); if (!(await fd.loadMoreConversations())) { hapticError(); toast.error('I couldn’t load older conversations. Try again.'); } }}>
                                {fd.conversationsBusy ? 'Loading…' : 'Older conversations'}
                            </button>
                        </div>
                    )}
                </>
            )}

            {filter === 'msg' && (
                <>
                    <NeedsCards fd={fd} groups={openPeople} act={needs.act} undoBar={needs.undoBar} searching={searching} kind={kind} onKind={setKind} look={look} />
                    {!!doneMsgs.length && (
                        <Section label="Done">
                            <div className="bly-stack">
                                {doneMsgs.map((m) => <MessageCard key={m.id} message={m} doneAt={fd.done[m.id]} onReopen={fd.markMessageOpen} />)}
                            </div>
                        </Section>
                    )}
                    {!openPeople.length && !doneMsgs.length && (
                        <p className="fd-empty">{searching ? `No messages match “${q.trim()}”.` : 'When I take a message for you, it shows up here.'}</p>
                    )}
                    {!!(openPeople.length + doneMsgs.length) && (
                        <p className="fd-device-note">Done is kept on this device for now, so a message you’ve cleared here can still show on your other devices.</p>
                    )}
                </>
            )}
            {needs.panels}

            {panel && panel.conversation && (
                <ConversationPanel conversation={panel.conversation} onClose={() => setPanel(null)} onTeach={() => setPanel({ add: true })} />
            )}
            {panel && panel.add && (
                <AnswerPanel
                    answers={fd.answers}
                    onAdd={({ question, answer }) => fd.addAnswer(question, answer)}
                    onChange={(old, next) => fd.changeAnswer(old, next)}
                    onRemove={(old) => fd.removeAnswer(old._id)}
                    onClose={() => setPanel(null)}
                />
            )}
        </div>
    );
}
